/**
 * 转出 - 验证码输入页面
 */

import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import { v4 as uuidv4 } from 'uuid'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import VerificationCodeInput from '@/components/VerificationCodeInput'
import { VerActionType } from '@/components/SelectVerification'
import { useSendCode } from '@/hooks/useSendCode'
import { useUserInfo } from '@/hooks'
import { APIWeb } from '@/api'

// 转出信息接口
interface WithdrawInfo {
  cardId: string
  cardNo: string
  amount: string
  currency: string
  receivedAmount: string
  fee: number
  verificationMethod: VerActionType
  account: string
}

export default React.memo(function WithdrawVerifyCode() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { userInfo } = useUserInfo()
  const { sendCode, sendCodeLoading } = useSendCode()
  
  // 从路由 state 获取转出信息
  const withdrawInfo: WithdrawInfo = state || {}
  
  const [submitLoading, setSubmitLoading] = useState(false)
  // 谷歌验证码
  const [googleCode, setGoogleCode] = useState('')

  // 是否是谷歌验证
  const isGoogleVerification = withdrawInfo.verificationMethod === VerActionType.GoogleVer

  // 谷歌验证码输入处理
  const handleGoogleCodeChange = useCallback((value: string) => {
    setGoogleCode(value)
  }, [])

  // 提交验证码
  const handleSubmit = useCallback(async (code: string) => {
    if (!withdrawInfo.cardId || !code) {
      Toast.info(t('参数错误'))
      return
    }

    try {
      setSubmitLoading(true)
      
      // 第一步：验证验证码并获取 verify_key
      const verifyResult = await APIWeb.verifyOldAccountCode({
        account: isGoogleVerification ? 'google' : withdrawInfo.account,
        scene: 'card_withdraw',
        code,
      })

      const verifyKey = verifyResult?.verify_key || code

      // 第二步：调用转出 API
      await APIWeb.withdrawCardV2({
        card_id: Number(withdrawInfo.cardId),
        amount: parseFloat(withdrawInfo.amount),
        pay_type: withdrawInfo.currency.toLowerCase(),
        verify_key: verifyKey,
        idempotency_key: uuidv4(),
      })
      
      // 跳转到成功页面
      navigate('/card-v2/card-withdraw/success', {
        replace: true,
        state: {
          amount: withdrawInfo.amount,
          currency: withdrawInfo.currency
        }
      })
    } catch (error: any) {
      console.error('转出失败:', error)
      Toast.fail(error?.message || t('转出失败'))
    } finally {
      setSubmitLoading(false)
    }
  }, [withdrawInfo, navigate, t, isGoogleVerification])

  // 重新发送验证码
  const handleResendCode = useCallback(async () => {
    if (isGoogleVerification) {
      return
    }

    const account = withdrawInfo.verificationMethod === VerActionType.PhoneVer
      ? userInfo?.phone || ''
      : userInfo?.email || ''

    await sendCode({
      account,
      scene: 'card_withdraw'
    })
  }, [isGoogleVerification, withdrawInfo.verificationMethod, userInfo, sendCode])

  // 谷歌验证器输入组件
  const googleInputVer = useMemo(() => {
    return (
      <div>
        <div className="space-y-2">
          <div className="text-3 text-[#1A1A1A]/60 mb-1">{t('请输入谷歌验证码')}</div>
          <CustomTextInput
            value={googleCode}
            onChangeText={handleGoogleCodeChange}
            placeholder={t('请输入谷歌验证码')}
          />
        </div>
        <div className="mt-6">
          <CustomButton
            loading={submitLoading}
            disabled={submitLoading || !googleCode}
            onClick={() => handleSubmit(googleCode)}
          >
            {t('确认转出')}
          </CustomButton>
        </div>
      </div>
    )
  }, [googleCode, handleGoogleCodeChange, submitLoading, handleSubmit, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('转出')}</span>}
      />

      <div className="px-6 py-6">
        {isGoogleVerification ? (
          googleInputVer
        ) : (
          <VerificationCodeInput
            account={withdrawInfo.account || ''}
            resendLoading={sendCodeLoading}
            submitLoading={submitLoading}
            onResendCode={handleResendCode}
            onSubmit={handleSubmit}
            submitButtonText={t('确认')}
          />
        )}
      </div>
    </PageLayout>
  )
})
